import { ChatDetailsResponse } from '@choco-admin/types';
import { Chip } from '@heroui/react';
import React, { useCallback } from 'react';
import { FiTruck, FiExternalLink, FiMail } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import { ChocoCheckbox } from '../../../../../../../components/FormComponents';
import { useNotification } from '../../../../../../../contexts/NotificationContext';
import { useUpdateChatNotificationPreferences } from '../../../../../hooks/useUpdateChatNotificationPreferences';

interface ChatDetailsWithNotifications extends ChatDetailsResponse {
  newOrder?: {
    deselectedUsersList?: string[];
  };
}

interface SupplierSectionProps {
  chatData: ChatDetailsWithNotifications;
  chatId: string;
}

export const SupplierSection: React.FC<SupplierSectionProps> = ({
  chatData,
  chatId,
}) => {
  const supplier = chatData.supplierEntity;
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { updateNotificationPreferences, isUpdating } =
    useUpdateChatNotificationPreferences({
      onSuccess: () => {
        addNotification({
          title: 'Notification Preferences Updated',
          message: 'Order notifications have been updated for this chat.',
          type: 'success',
        });
      },
      onError: (error) => {
        addNotification({
          title: 'Update Failed',
          message: error.message,
          type: 'error',
        });
      },
    });

  const handleNotificationChange = useCallback(
    async (isSelected: boolean) => {
      if (!supplier?.id) return;

      await updateNotificationPreferences({
        chatId,
        supplierId: supplier.id,
        isEnabled: isSelected,
      });
    },
    [chatId, supplier?.id, updateNotificationPreferences]
  );

  const handleSupplierClick = () => {
    if (supplier?.id) {
      navigate(`/suppliers/${supplier.id}`);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <FiTruck className="w-6 h-6 text-secondary" />
        <div>
          <h3 className="text-xl font-semibold text-foreground">Supplier</h3>
          <p className="text-sm text-foreground-500">
            Primary supplier for this chat
          </p>
        </div>
      </div>

      {supplier ? (
        <div
          className="flex items-center justify-between p-4 bg-content2 rounded-lg cursor-pointer hover:bg-content3 transition-colors group"
          onClick={handleSupplierClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleSupplierClick();
            }
          }}
        >
          <div className="flex-1">
            <p className="text-lg font-medium text-foreground group-hover:text-primary transition-colors">
              {supplier.name}
            </p>
            {supplier.referenceId && (
              <p className="text-sm text-foreground-500 mt-1">
                ID: {supplier.referenceId}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Chip color="success" variant="flat" size="md">
              Active
            </Chip>
            <FiExternalLink className="w-4 h-4 text-foreground-400 group-hover:text-primary transition-colors" />
          </div>
        </div>
      ) : (
        <div className="p-4 bg-content2 rounded-lg">
          <p className="text-foreground-500 italic">No supplier assigned</p>
        </div>
      )}

      {/* Notification Preferences */}
      <div className="mt-8 space-y-4">
        <div className="flex items-center gap-3">
          <FiMail className="w-6 h-6 text-secondary" />
          <div>
            <h3 className="text-xl font-semibold text-foreground">
              Notification Preferences
            </h3>
            <p className="text-sm text-foreground-500">
              Configure order notifications for this chat
            </p>
          </div>
        </div>

        <div className="p-4 bg-content2 rounded-lg space-y-4">
          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">
              Receive order transmissions and messages for this chat:
            </p>
            <p className="text-sm font-mono text-foreground-500">
              smoke-tests+107237ea-83ba-407c-895c-7597226fc454@spezi.app
            </p>
          </div>

          <ChocoCheckbox
            label="Enable order notifications"
            description="Receive notifications for new orders and messages in this chat"
            isSelected={chatData.newOrder?.deselectedUsersList?.length === 0}
            onValueChange={handleNotificationChange}
            isDisabled={isUpdating}
          />
        </div>
      </div>
    </div>
  );
};
